import List from 'antd/lib/list';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import {
    defaultOptionsForQuickStartCIScanAttribute,
    displayNameForAttributeAndValue,
    doesQuickStartCIScanAttributeCanBeManuallyEdited,
    getQuickStartCIExtraAttributesForPlatform,
    getQuickStartCIScanAttributesForPlatform,
} from '../../helpers/displayHelpers';
import { RootState } from '../../reducers/reducers';
import { selectAppPlatform } from '../../selectors/selectors';
import { QuickStartCIScanAttribute, ScanResult } from '../../types';
import { AppComponentProps } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import Select from 'antd/lib/select';
import Autocomplete from 'antd/lib/auto-complete';
import Input from 'antd/lib/input';
import Form, { FormInstance } from 'antd/lib/form';
import Icon, { InfoCircleOutlined, CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import {
    iconForQuickStartCIScanAttribute,
    displayNameForQuickStartCIScanAttribute,
    descriptionForQuickStartCIScanAttribute,
    doesQuickStartCIScanAttributeAcceptMultipleValues,
} from '../../helpers/displayHelpers';
import { antGrey, runwayColors } from '../styles/globalStyles';
import Spin from 'antd/lib/spin';
import Button from 'antd/lib/button';

export interface ScanListItemProps {
    scanAttribute: QuickStartCIScanAttribute;
    detectedValues?: string[];
    notDetected?: boolean;
    form?: FormInstance;
    isLoading?: boolean;
}

export const ScanListItem: React.FC<ScanListItemProps> = ({
    form,
    scanAttribute,
    detectedValues,
    notDetected,
    isLoading,
}) => {
    let detailView: React.ReactNode = null;

    const [isInEditMode, setIsInEditMode] = useState(false);

    if (form && (!detectedValues || detectedValues.length > 1 || isInEditMode)) {
        // if we have multiple detected values or we couldn't detect any values, we need to show a select or input
        let inputComponent: React.ReactNode = null;
        if (detectedValues?.length > 1) {
            const canBeManuallyEdited = doesQuickStartCIScanAttributeCanBeManuallyEdited(scanAttribute);

            if (canBeManuallyEdited) {
                inputComponent = (
                    <Autocomplete
                        placeholder={placeholderForScanAttribute(scanAttribute)}
                        options={detectedValues.map((value) => ({
                            label: displayNameForAttributeAndValue(scanAttribute, value),
                            value,
                        }))}
                    />
                );
            } else {
                const acceptsMultipleValues = doesQuickStartCIScanAttributeAcceptMultipleValues(scanAttribute);

                inputComponent = (
                    <Select
                        placeholder={placeholderForScanAttribute(scanAttribute)}
                        mode={
                            acceptsMultipleValues ?
                                canBeManuallyEdited ?
                                    'tags'
                                :   'multiple'
                            :   undefined
                        }
                        options={detectedValues.map((value) => ({
                            label: displayNameForAttributeAndValue(scanAttribute, value),
                            value,
                        }))}
                    />
                );
            }
        } else {
            inputComponent = <Input placeholder={placeholderForScanAttribute(scanAttribute)} />;
        }

        detailView = (
            <div
                style={{
                    flex: 1,
                    maxWidth: 300,
                }}
            >
                <Form.Item
                    style={{
                        marginBottom: 0,
                    }}
                    name={scanAttribute}
                >
                    {inputComponent}
                </Form.Item>
            </div>
        );
    } else if (isLoading) {
        detailView = <Spin size="small" />;
    } else if (notDetected) {
        detailView = (
            <Space>
                <Text
                    code
                    style={{
                        color: antGrey.grey6,
                    }}
                >
                    Not found
                </Text>
                <CloseOutlined
                    style={{
                        color: antGrey.grey6,
                    }}
                />
            </Space>
        );
    } else if (detectedValues?.length === 1) {
        detailView = (
            <Space>
                {form && (
                    <>
                        <Button icon={<EditOutlined />} type="link" onClick={() => setIsInEditMode(true)} />
                        <Form.Item
                            hidden
                            style={{
                                marginBottom: 0,
                            }}
                            name={scanAttribute}
                        ></Form.Item>
                    </>
                )}
                <Text
                    code
                    style={{
                        color: antGrey.grey8,
                    }}
                >
                    {detectedValues[0]}
                </Text>
                <CheckOutlined
                    style={{
                        color: runwayColors.statusGreen,
                    }}
                />
            </Space>
        );
    } else if (detectedValues?.length > 1) {
        detailView = (
            <Space>
                <Text
                    code
                    style={{
                        color: antGrey.grey8,
                    }}
                >
                    multiple found
                </Text>
                <CheckOutlined
                    style={{
                        color: runwayColors.statusGreen,
                    }}
                />
            </Space>
        );
    }

    return (
        <div
            style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}
        >
            <Space size={20}>
                <Icon
                    style={{
                        color: antGrey.grey5,
                        fontSize: 22,
                        marginTop: 8,
                        marginBottom: 4,
                    }}
                    component={iconForQuickStartCIScanAttribute(scanAttribute)}
                />
                <Space size={10}>
                    <Text
                        style={{
                            color: antGrey.grey8,
                            fontWeight: 600,
                            fontSize: 16,
                            padding: '4px 0',
                        }}
                    >
                        {displayNameForQuickStartCIScanAttribute(scanAttribute)}
                    </Text>
                    <Tooltip title={descriptionForQuickStartCIScanAttribute(scanAttribute)} placement="right">
                        <InfoCircleOutlined
                            style={{
                                color: antGrey.grey6,
                            }}
                        />
                    </Tooltip>
                </Space>
            </Space>
            {detailView}
        </div>
    );
};

export interface ScanListProps extends AppComponentProps {
    form?: FormInstance;
    scanResult?: ScanResult;
    isLoading?: boolean;
}

export const ScanList: React.FC<ScanListProps> = ({ appId, scanResult, isLoading, form }) => {
    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));

    const attributes = getQuickStartCIScanAttributesForPlatform(platform);

    let dataSource = form ? [...attributes, ...getQuickStartCIExtraAttributesForPlatform(platform)] : attributes;

    if (!scanResult?.isReactNativeApp) {
        dataSource = dataSource.filter((attribute) => attribute !== QuickStartCIScanAttribute.NodeVersion);
    }

    return (
        <List
            bordered
            style={{
                backgroundColor: runwayColors.white,
            }}
            dataSource={dataSource}
            renderItem={(item) => {
                const rawDetectedValue =
                    (scanResult && defaultOptionsForQuickStartCIScanAttribute(item)) ?? scanResult?.[item];

                let detectedValues = null;
                if (rawDetectedValue) {
                    detectedValues = Array.isArray(rawDetectedValue) ? rawDetectedValue : [rawDetectedValue];
                }

                if (item === QuickStartCIScanAttribute.AppBundleIdentifier) {
                    const uniqueValues = Array.from(
                        new Set([
                            ...(detectedValues ?? []),
                            ...(scanResult?.[QuickStartCIScanAttribute.BundleIdentifiers] ?? []),
                        ]),
                    );
                    detectedValues = uniqueValues;
                }

                const notDetected = !!scanResult && !detectedValues;

                return (
                    <List.Item>
                        <ScanListItem
                            scanAttribute={item}
                            detectedValues={detectedValues}
                            isLoading={isLoading}
                            notDetected={notDetected}
                            form={form}
                        />
                    </List.Item>
                );
            }}
        />
    );
};

const placeholderForScanAttribute = (attribute: QuickStartCIScanAttribute) => {
    switch (attribute) {
        case QuickStartCIScanAttribute.AppBundleIdentifier:
            return 'com.company.app-name';
        case QuickStartCIScanAttribute.BuildConfigurations:
            return 'Release';
        case QuickStartCIScanAttribute.BuildFlavor:
            return 'vanilla';
        case QuickStartCIScanAttribute.BuildType:
            return 'release';
        case QuickStartCIScanAttribute.PackageName:
            return 'com.company.app-name';
        case QuickStartCIScanAttribute.NodeVersion:
            return '15.0.0';
        case QuickStartCIScanAttribute.RubyVersion:
            return '3.2.1';
        case QuickStartCIScanAttribute.ProjectDirectory:
            return '/app';
        case QuickStartCIScanAttribute.WorkspacePath:
            return '/';
        case QuickStartCIScanAttribute.Schemes:
            return 'Select scheme';
        case QuickStartCIScanAttribute.Targets:
            return 'Select targets';
        default:
            return null;
    }
};
